import { create } from 'zustand';
import type { SearchResponse, AdvancedFilters, RecommanderBucket } from '@mtg-deck-tool/shared';
import { RECOMMANDER_BUCKET_SLUGS } from '@mtg-deck-tool/shared';
import type { SearchFilters } from '../lib/query-builder.js';

export const DEFAULT_ADVANCED_FILTERS: AdvancedFilters = {
  cardName: '',
  oracleText: '',
  types: [],
  colors: { selected: [], mode: 'including' },
  manaCost: '',
  stats: { power: null, toughness: null, manaValue: null },
  set: '',
  rarity: '',
};

interface SearchStoreState {
  // ── Query state ──
  userInput: string;
  filters: SearchFilters;
  activeTagSlugs: string[];
  /** Currently-pinned recommander bucket (incl. 'Top'), mirrored from a `rectag:<slug>` clause in userInput. */
  activeRecBucket: RecommanderBucket | null;

  // ── Sort state ──
  sortOrder: string;
  sortDir: 'asc' | 'desc';

  // ── Advanced panel ──
  isAdvancedOpen: boolean;
  advancedFilters: AdvancedFilters;

  // ── Results state ──
  results: SearchResponse | null;
  currentPage: number;
  isLoading: boolean;
  error: string | null;

  // ── Programmatic search trigger ──
  // Incrementing this causes SearchBar to fire execute() on the next render.
  searchTrigger: number;

  // ── Actions ──
  setUserInput: (input: string) => void;
  triggerSearch: () => void;
  setFilters: (filters: Partial<SearchFilters>) => void;
  clearFilters: () => void;
  toggleTagFilter: (slug: string, multi: boolean) => void;
  /** Toggle a recommander bucket on/off — writes/strips a `rectag:<slug>` clause in the search bar. */
  toggleRecBucket: (bucket: RecommanderBucket) => void;
  /** Clear any active rectag clause without changing other state. */
  clearRecBucket: () => void;
  setSortOrder: (order: string, dir: 'asc' | 'desc') => void;
  setAdvancedOpen: (open: boolean) => void;
  setAdvancedFilter: <K extends keyof AdvancedFilters>(key: K, value: AdvancedFilters[K]) => void;
  clearAdvancedFilters: () => void;
  setResults: (results: SearchResponse | null, page?: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (err: string | null) => void;
  clearSearch: () => void;
}

export const useSearchStore = create<SearchStoreState>()((set) => ({
  userInput: '',
  searchTrigger: 0,
  filters: {},
  activeTagSlugs: [],
  activeRecBucket: null,
  sortOrder: 'edhrec',
  sortDir: 'asc',
  isAdvancedOpen: false,
  advancedFilters: { ...DEFAULT_ADVANCED_FILTERS },
  results: null,
  isLoading: false,
  error: null,
  currentPage: 1,

  setUserInput: (userInput) => set({ userInput }),

  triggerSearch: () => set((state) => ({ searchTrigger: state.searchTrigger + 1 })),

  setFilters: (partial) =>
    set((state) => ({ filters: { ...state.filters, ...partial } })),

  clearFilters: () => set({ filters: {}, userInput: '' }),

  toggleTagFilter: (slug, multi) =>
    set((state) => {
      let nextSlugs: string[];
      if (multi) {
        const has = state.activeTagSlugs.includes(slug);
        nextSlugs = has ? state.activeTagSlugs.filter((s) => s !== slug) : [...state.activeTagSlugs, slug];
      } else {
        const isOnlyActive = state.activeTagSlugs.length === 1 && state.activeTagSlugs[0] === slug;
        nextSlugs = isOnlyActive ? [] : [slug];
      }

      // Write otag: clauses into the search bar
      // Strip any existing otag: clauses and (otag:... OR ...) groups from the bar
      let bar = state.userInput
        .replace(/\(otag:\S+(?:\s+OR\s+otag:\S+)*\)/g, '')
        .replace(/otag:\S+/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

      if (nextSlugs.length === 1) {
        bar = bar ? `${bar} otag:${nextSlugs[0]}` : `otag:${nextSlugs[0]}`;
      } else if (nextSlugs.length > 1) {
        const group = `(${nextSlugs.map((s) => `otag:${s}`).join(' OR ')})`;
        bar = bar ? `${bar} ${group}` : group;
      }

      // If bar is empty after removing tags, clear results too (back to deck view)
      if (!bar) {
        return { activeTagSlugs: nextSlugs, userInput: bar, results: null, error: null, currentPage: 1 };
      }
      return { activeTagSlugs: nextSlugs, userInput: bar };
    }),

  toggleRecBucket: (bucket) =>
    set((state) => {
      const isActive = state.activeRecBucket === bucket;
      const nextBucket = isActive ? null : bucket;

      // Strip any existing rectag: clauses from the bar — buckets are single-select.
      let bar = state.userInput
        .replace(/rectag:\S+/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

      if (nextBucket) {
        const slug = RECOMMANDER_BUCKET_SLUGS[nextBucket];
        bar = bar ? `${bar} rectag:${slug}` : `rectag:${slug}`;
      }

      // Empty bar → clear results too, mirroring toggleTagFilter's behavior.
      if (!bar) {
        return { activeRecBucket: nextBucket, userInput: bar, results: null, error: null, currentPage: 1 };
      }
      return { activeRecBucket: nextBucket, userInput: bar };
    }),

  clearRecBucket: () =>
    set((state) => {
      const bar = state.userInput
        .replace(/rectag:\S+/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      if (!bar) {
        return { activeRecBucket: null, userInput: bar, results: null, error: null, currentPage: 1 };
      }
      return { activeRecBucket: null, userInput: bar };
    }),

  setSortOrder: (sortOrder, sortDir) => set({ sortOrder, sortDir }),

  setAdvancedOpen: (isAdvancedOpen) => set({ isAdvancedOpen }),

  setAdvancedFilter: (key, value) =>
    set((state) => ({
      advancedFilters: { ...state.advancedFilters, [key]: value },
    })),

  clearAdvancedFilters: () =>
    set({ advancedFilters: { ...DEFAULT_ADVANCED_FILTERS } }),

  setResults: (results, page = 1) => set({ results, error: null, currentPage: page, isLoading: false }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  clearSearch: () =>
    set({ userInput: '', filters: {}, activeTagSlugs: [], activeRecBucket: null, isAdvancedOpen: false, advancedFilters: { ...DEFAULT_ADVANCED_FILTERS }, results: null, error: null, currentPage: 1 }),

}));
